import React from 'react';
import { GaugeIcon } from 'lucide-react';
import { BASE_RULES_SUMMARY, BONUS_CAP_NOTE, BONUS_SANDBOX_NOTE, SELF_GEN_BONUS_RULE, TIER_LABEL_NOTE } from '../../data/statement';
import { Band } from '../../types/commission';
import { formatUsd } from '../../utils/format';
import { Card } from '../sf/Card';
import { RiskNote } from '../sf/RiskNote';
import { UnderReviewNote } from '../sf/UnderReviewNote';
import { BandBar } from './BandBar';
interface TierProgressProps {
  branch: string;
  volume: number;
  unitsThisMonth: number;
  baseBands: Band[];
  bonusBands: Band[];
}
export function TierProgress({
  branch,
  volume,
  unitsThisMonth,
  baseBands,
  bonusBands
}: TierProgressProps) {
  const volumeM = volume / 1000000;
  const baseBand = baseBands.find(band => volumeM >= band.from && (band.to === null || volumeM < band.to));
  const bonusBand = bonusBands.find(band => unitsThisMonth >= band.from && (band.to === null || unitsThisMonth < band.to));
  return <Card title="Base BPS (branch + period volume) & monthly bonus" icon={GaugeIcon} iconBg="#0176D3" meta="LO Standard Plan v1 parity" data-id="element-1086" data-mp="2sad13wl5yh8i">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2" data-id="element-1087" data-mp="2nilhy74c7dse">
        <section aria-label="Base BPS" data-id="element-1088" data-mp="2ikjrv28hue7f">
          <p className="text-[13px] font-semibold" data-id="element-1089" data-mp="2ilbli7ij61v2">
            Base: {baseBand?.valueLabel}{' '}
            <span className="font-normal text-sf-weak" data-id="element-1090" data-mp="2dmry02oagi0l">
              · {branch} · October volume {formatUsd(volume)} ≥ $1,500,000.00
            </span>
          </p>
          <p className="text-xs text-sf-weak" data-id="element-1091" data-mp="36ae2dd5cbqoi">Standard LO base by branch and the employee’s total loan volume for the period: {BASE_RULES_SUMMARY}.</p>
          <div className="mt-2" data-id="element-1092" data-mp="319gmj77sz8q6">
            <BandBar bands={baseBands} value={volumeM} scaleMax={3} markerLabel={`October ${formatUsd(volume)}`} ariaLabel={`${branch} period volume ${formatUsd(volume)}, ${baseBand?.valueLabel} base`} data-id="element-1093" data-mp="2wbyj1wck1lpk" />
          </div>
          <div className="mt-2 space-y-1.5" data-id="element-1094" data-mp="cxci6l9ah52g">
            <RiskNote data-id="element-1095" data-mp="cynoarq7iowq" />
            <p className="text-[11px] text-sf-weak" data-id="element-1096" data-mp="cy47xfg7g8kt">{TIER_LABEL_NOTE}</p>
          </div>
        </section>
        <section aria-label="Monthly bonus" data-id="element-1097" data-mp="gwtplk8a0ca9">
          <p className="text-[13px] font-semibold" data-id="element-1098" data-mp="2zy3or6xeqcs">
            Monthly Bonus: 0 bps{' '}
            <span className="font-normal text-sf-weak" data-id="element-1099" data-mp="7xq64f575uv8">
              · 7+ band value is 0 (main) · {unitsThisMonth} closed loans in October
            </span>
          </p>
          <p className="text-xs text-sf-weak" data-id="element-1100" data-mp="3pcwlyttxueja">Driven by the count of closed loans in the month — not funded units, not volume — and applied to every loan this month.</p>
          <div className="mt-2" data-id="element-1101" data-mp="3gfmd1f4lf1hj">
            <BandBar bands={bonusBands} value={unitsThisMonth} scaleMax={9} discrete markerLabel={`${unitsThisMonth} closed loans`} ariaLabel={`${unitsThisMonth} closed loans this month, ${bonusBand?.valueLabel} bonus`} data-id="element-1102" data-mp="3ld5pqaln767d" />
          </div>
          <p className="mt-2 text-xs text-sf-weak" data-id="element-1103" data-mp="2rseo8300cflu">
            {unitsThisMonth} closed loans → {bonusBand?.valueLabel}, so base + bonus = 125 bps on all {unitsThisMonth} October loans. Cap not binding.
          </p>
          <p className="mt-1 text-[11px] text-sf-weak" data-id="element-1104" data-mp="31s5fzwx6ogi8">
            {BONUS_SANDBOX_NOTE} {BONUS_CAP_NOTE} {SELF_GEN_BONUS_RULE}
          </p>
        </section>
      </div>
      <div className="mt-4" data-id="element-1105" data-mp="3arir0u0yhd52">
        <UnderReviewNote data-id="element-1106" data-mp="3aqpsggc5hty0" />
      </div>
    </Card>;
}